/**
 * The manufacturability pass.
 *
 * Walks a board once, flattening components into world space, and measures it
 * against {@link DfmRules}. What comes back is a list of findings, each with the
 * rule it broke, the number required, the number measured, and a point to jump
 * the view to — because a fault you cannot find on a dense board is a fault you
 * cannot fix.
 *
 * **Clearance is delegated.** `net/clearance.ts` already sweeps copper against
 * copper with a bucketed grid and knows which primitives share a net; running a
 * second sweep here would be a second opinion on the same question. It is asked
 * with the fab's single clearance number in place of the file's three.
 *
 * **Everything is measured, nothing is fixed.** A DFM tool that edits the board
 * is a different and much riskier thing: the answer to a thin annular ring is
 * usually a smaller drill, but sometimes a bigger pad and sometimes moving the
 * part, and only the person designing it knows which.
 */

import {
  LayerType, padIsSchematic, padIsThrough,
  type CadDocument, type ComponentEntity, type Entity, type PadEntity,
} from '../model.ts';
import { PAD_MASTER_LAYER, outlineEntities } from '../geom/board.ts';
import { outlineRing } from '../geom/outline.ts';
import { clearanceViolations } from '../net/clearance.ts';
import {
  DEFAULT_DFM, RULE_LABELS, type DfmRules, type RuleId, type Severity,
} from './rules.ts';

export interface DfmFinding {
  rule: RuleId;
  severity: Severity;
  /** One line, with both numbers in it. Units are applied by the caller. */
  detail: string;
  /** The rule's figure, in inches. */
  required: number;
  /** What was measured, in inches. Absent where the rule is a yes or no. */
  actual?: number;
  /** Where to look. Absent only for a fault about the board as a whole. */
  at?: { x: number; y: number };
  /** What to select when the view jumps there. */
  entityIds: number[];
  layer?: number;
}

export interface DfmReport {
  findings: DfmFinding[];
  errors: number;
  warnings: number;
  /** Rules that ran and found nothing, so a clean report says what it checked. */
  clean: RuleId[];
  /** Rules that could not run, and why — an unchecked rule is not a passed one. */
  skipped: { rule: RuleId; why: string }[];
}

/** A primitive lifted into world space, with the part it came from. */
interface Placed {
  e: Entity;
  owner: ComponentEntity | null;
  /** Component rotation applied, so a turned footprint measures as it sits. */
  x: number;
  y: number;
  /**
   * Every point of it that matters, in world space.
   *
   * A line has two ends and they can be inches apart. Measuring a track's
   * distance to the board edge from its middle, and allowing for its length,
   * made every long trace on the board look as though it reached the edge —
   * 758 findings across the samples, nearly all of them invented.
   */
  pts: { x: number; y: number }[];
}

/** Walk the tree once, resolving component frames. */
function flatten(doc: CadDocument): Placed[] {
  const out: Placed[] = [];
  const walk = (
    list: Entity[], ox: number, oy: number, deg: number,
    mirror: number, owner: ComponentEntity | null,
  ): void => {
    const r = (deg * Math.PI) / 180;
    const c = Math.cos(r);
    const s = Math.sin(r);
    const place = (x: number, y: number): { x: number; y: number } => {
      const mx = x * mirror;
      return { x: ox + mx * c - y * s, y: oy + mx * s + y * c };
    };
    for (const e of list) {
      if (e.kind === 'component') {
        const at = place(e.x, e.y);
        walk(e.children, at.x, at.y, deg + (e.rotation ?? 0),
          mirror * (e.mirrored ? -1 : 1), e);
        continue;
      }
      const pts = e.kind === 'pad' || e.kind === 'text' ? [place(e.x, e.y)]
        : e.kind === 'line' ? [place(e.x1, e.y1), place(e.x2, e.y2)]
          : e.kind === 'arc' ? [place(e.cx, e.cy)]
            : e.kind === 'fill' ? e.vertices.map((v) => place(v.x, v.y))
              : [];
      if (pts.length === 0) continue;
      out.push({ e, owner, x: pts[0]!.x, y: pts[0]!.y, pts });
    }
  };
  walk(doc.entities, 0, 0, 0, 1, null);
  return out;
}

/** Shortest distance from a point to a segment. */
function toSegment(
  px: number, py: number, x1: number, y1: number, x2: number, y2: number,
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = dx * dx + dy * dy;
  if (len === 0) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / len;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

/** A pad's own radius, near enough for a clearance measured to its centre. */
function padReach(p: PadEntity): number {
  return Math.max(p.sizeX, p.sizeY) / 2;
}

/**
 * Check a board.
 *
 * `rules` defaults to the document's own, then to the standard profile — the
 * same fall-through the netscan record uses, so a file that carries its shop's
 * numbers is checked against them without being asked.
 */
export function dfmCheck(
  doc: CadDocument, rules: DfmRules = doc.dfm ?? DEFAULT_DFM,
): DfmReport {
  const findings: DfmFinding[] = [];
  const skipped: { rule: RuleId; why: string }[] = [];
  const ran = new Set<RuleId>();

  if (doc.kind !== 'pcb') {
    return {
      findings: [], errors: 0, warnings: 0, clean: [],
      skipped: [{ rule: 'outline', why: 'this is a schematic, not a board' }],
    };
  }

  const copper = new Set(
    doc.layers.map((l, i) => ({ l, i }))
      .filter(({ l }) => l.type === LayerType.Copper || l.type === LayerType.Plane)
      .map(({ i }) => i));
  // Not the pad master. CIRCAD types layer 0 as Silk, and it is not silkscreen
  // at all — it is where pads and the parts' own lettering live, none of which
  // is printed. Counting it turned every reference designator inside every
  // footprint into a silkscreen finding: 2539 of them across the samples,
  // against 40-odd real ones.
  const silk = new Set(
    doc.layers.map((l, i) => ({ l, i }))
      .filter(({ l, i }) => l.type === LayerType.Silk && i !== PAD_MASTER_LAYER)
      .map(({ i }) => i));

  const items = flatten(doc);
  const add = (f: DfmFinding): void => { findings.push(f); ran.add(f.rule); };

  // --- The board has an edge -------------------------------------------------
  ran.add('outline');
  const ring = outlineRing(doc);
  if (outlineEntities(doc).length === 0) {
    findings.push({
      rule: 'outline', severity: 'error', required: 0, entityIds: [],
      detail: 'No board outline has been drawn, so the board has no size and no shape.',
    });
  }

  // --- Copper and silkscreen line widths ------------------------------------
  ran.add('track-width');
  ran.add('silk-width');
  ran.add('silk-height');
  for (const { e, x, y } of items) {
    if (e.kind === 'line' || e.kind === 'arc') {
      const w = e.width;
      if (copper.has(e.layer) && w > 0 && w < rules.minTrackWidth) {
        add({
          rule: 'track-width', severity: 'error',
          required: rules.minTrackWidth, actual: w,
          detail: 'A copper track is thinner than the process holds.',
          at: { x, y }, entityIds: [e.id], layer: e.layer,
        });
      }
      if (silk.has(e.layer) && w > 0 && w < rules.minSilkWidth) {
        add({
          rule: 'silk-width', severity: 'warning',
          required: rules.minSilkWidth, actual: w,
          detail: 'A silkscreen line is thinner than the screen resolves.',
          at: { x, y }, entityIds: [e.id], layer: e.layer,
        });
      }
    }
    if (e.kind === 'text' && silk.has(e.layer) && e.height < rules.minSilkHeight) {
      add({
        rule: 'silk-height', severity: 'warning',
        required: rules.minSilkHeight, actual: e.height,
        detail: `Silkscreen lettering "${e.text.slice(0, 20)}" is too small to print.`,
        at: { x, y }, entityIds: [e.id], layer: e.layer,
      });
    }
  }

  // --- Holes: size, and the copper left round them ---------------------------
  ran.add('drill-min');
  ran.add('drill-max');
  ran.add('annular-ring');
  const drilled: { p: PadEntity; x: number; y: number }[] = [];
  for (const { e, x, y } of items) {
    if (e.kind !== 'pad') continue;
    const pad = e;
    // A schematic pin carries the through letter and is not a hole in a board.
    const hole = padIsThrough(pad.type) && !padIsSchematic(pad.type)
      ? (pad.hole ?? 0) : 0;
    if (hole <= 0) continue;
    drilled.push({ p: pad, x, y });

    if (hole < rules.minDrill) {
      add({
        rule: 'drill-min', severity: 'error', required: rules.minDrill, actual: hole,
        detail: 'A hole is smaller than the smallest drill.',
        at: { x, y }, entityIds: [pad.id],
      });
    } else if (hole > rules.maxDrill) {
      add({
        rule: 'drill-max', severity: 'warning', required: rules.maxDrill, actual: hole,
        detail: 'A hole is larger than the largest drill and would have to be routed.',
        at: { x, y }, entityIds: [pad.id],
      });
    }

    // The ring is measured on the narrow axis: an oblong pad is only as good
    // as its tightest side, which is where an off-centre drill breaks out.
    const ringWidth = (Math.min(pad.sizeX, pad.sizeY) - hole) / 2;
    if (ringWidth < rules.minAnnularRing) {
      add({
        rule: 'annular-ring',
        // Copper that does not reach round the hole at all is not a tight ring,
        // it is a hole through nothing — a different and worse fault.
        severity: ringWidth <= 0 ? 'error' : 'warning',
        required: rules.minAnnularRing, actual: ringWidth,
        detail: ringWidth <= 0
          ? 'A hole is as large as its pad, leaving no copper round it.'
          : 'Too little copper is left round a hole.',
        at: { x, y }, entityIds: [pad.id],
      });
    }
  }

  // --- Hole to hole ----------------------------------------------------------
  //
  // Bucketed on the rule, as the clearance sweep is, so a board of two thousand
  // holes is neighbours-only rather than four million comparisons.
  ran.add('hole-to-hole');
  {
    const reach = rules.minHoleToHole + rules.maxDrill;
    const cell = Math.max(reach, 0.05);
    const buckets = new Map<string, number[]>();
    const key = (x: number, y: number): string =>
      `${Math.floor(x / cell)}:${Math.floor(y / cell)}`;
    drilled.forEach((d, i) => {
      const k = key(d.x, d.y);
      const list = buckets.get(k);
      if (list) list.push(i); else buckets.set(k, [i]);
    });

    const seen = new Set<string>();
    drilled.forEach((a, i) => {
      const cx = Math.floor(a.x / cell);
      const cy = Math.floor(a.y / cell);
      for (let gx = cx - 1; gx <= cx + 1; gx++) {
        for (let gy = cy - 1; gy <= cy + 1; gy++) {
          for (const j of buckets.get(`${gx}:${gy}`) ?? []) {
            if (j <= i) continue;
            const b = drilled[j]!;
            const gap = Math.hypot(b.x - a.x, b.y - a.y)
              - (a.p.hole ?? 0) / 2 - (b.p.hole ?? 0) / 2;
            if (gap >= rules.minHoleToHole) continue;
            const pair = `${Math.min(a.p.id, b.p.id)}:${Math.max(a.p.id, b.p.id)}`;
            if (seen.has(pair)) continue;
            seen.add(pair);
            add({
              rule: 'hole-to-hole', severity: gap <= 0 ? 'error' : 'warning',
              required: rules.minHoleToHole, actual: gap,
              detail: gap <= 0
                ? 'Two holes overlap.'
                : 'Two holes leave too thin a wall between them.',
              at: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
              entityIds: [a.p.id, b.p.id],
            });
          }
        }
      }
    });
  }

  // --- Copper to the board edge ----------------------------------------------
  if (!ring || ring.pts.length < 3) {
    skipped.push({ rule: 'edge-clearance', why: 'there is no board outline to measure to' });
  } else {
    ran.add('edge-clearance');
    const pts = ring.pts;
    const nearest = (x: number, y: number): number => {
      let best = Infinity;
      for (let i = 0; i < pts.length; i++) {
        const a = pts[i]!;
        const b = pts[(i + 1) % pts.length]!;
        best = Math.min(best, toSegment(x, y, a[0], a[1], b[0], b[1]));
      }
      return best;
    };
    for (const { e, pts } of items) {
      if (!copper.has(e.layer) && !(e.kind === 'pad')) continue;
      const own = e.kind === 'pad' ? padReach(e)
        : e.kind === 'line' || e.kind === 'arc' ? e.width / 2 : 0;
      // Whichever end of it comes closest, less its own half-width.
      let gap = Infinity;
      let at = pts[0]!;
      for (const q of pts) {
        const d = nearest(q.x, q.y) - own;
        if (d < gap) { gap = d; at = q; }
      }
      if (gap >= rules.minEdgeClearance) continue;
      const { x, y } = at;
      add({
        rule: 'edge-clearance', severity: gap <= 0 ? 'error' : 'warning',
        required: rules.minEdgeClearance, actual: gap,
        detail: gap <= 0
          ? 'Copper crosses the board edge.'
          : 'Copper is too close to the board edge.',
        at: { x, y }, entityIds: [e.id], layer: e.layer,
      });
    }
  }

  // --- Clearance, from the engine that already does it -----------------------
  ran.add('clearance');
  for (const v of clearanceViolations(doc, {
    lineToLine: rules.minClearance,
    lineToPad: rules.minClearance,
    padToPad: rules.minClearance,
    ratLine: 0,
  })) {
    add({
      rule: 'clearance', severity: v.actual <= 0 ? 'error' : 'warning',
      required: v.required, actual: v.actual,
      detail: v.actual <= 0
        ? `Nets ${v.nets[0]} and ${v.nets[1]} touch.`
        : `Nets ${v.nets[0]} and ${v.nets[1]} are too close.`,
      at: v.at, entityIds: [...v.entityIds], layer: v.layer,
    });
  }

  const broken = new Set(findings.map((f) => f.rule));
  return {
    findings,
    errors: findings.filter((f) => f.severity === 'error').length,
    warnings: findings.filter((f) => f.severity === 'warning').length,
    clean: [...ran].filter((r) => !broken.has(r)),
    skipped,
  };
}

/** The rule's own title, for a report row. */
export function ruleTitle(r: RuleId): string {
  return RULE_LABELS[r].title;
}
